import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { getCityBySlug, generateCityMetadata } from '@/data/cities';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/primitives/Badge';
import { 
  MapPin, 
  Users, 
  Snowflake, 
  Star, 
  Clock, 
  Shield, 
  Check,
  Zap,
  DollarSign,
  Phone,
  ChevronRight
} from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

export function CityLandingPage() {
  const { citySlug } = useParams<{ citySlug: string }>();
  const navigate = useNavigate();
  const city = citySlug ? getCityBySlug(citySlug) : undefined;

  useEffect(() => {
    if (citySlug && city) {
      trackEvent('city_page_view', { city: city.name, province: city.provinceCode });
    }
  }, [citySlug, city]);

  // If city not found, redirect to home
  if (!citySlug || !city) {
    useEffect(() => {
      navigate('/');
    }, [navigate]);
    return null;
  }

  const metadata = generateCityMetadata(city);

  const handleGetApp = () => {
    trackEvent('city_cta_click', { 
      city: city.name, 
      cta_type: 'get_app',
      location: 'hero'
    });
    document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleQuoteClick = () => {
    trackEvent('city_cta_click', { 
      city: city.name, 
      cta_type: 'get_quote',
      location: 'pricing'
    });
    document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords} />
        <link rel="canonical" href={`https://snowblowr.ca/cities/${city.slug}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content={metadata.openGraph.title} />
        <meta property="og:description" content={metadata.openGraph.description} />
        <meta property="og:type" content={metadata.openGraph.type} />
        <meta property="og:locale" content={metadata.openGraph.locale} />
        <meta property="og:url" content={`https://snowblowr.ca/cities/${city.slug}`} />
        <meta property="og:image" content={metadata.openGraph.image} />
        <meta property="og:image:secure_url" content={metadata.openGraph.image} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`SNOWBLOWR - On-Demand Snow Removal in ${city.name}`} />
        <meta property="og:site_name" content="SNOWBLOWR" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`https://snowblowr.ca/cities/${city.slug}`} />
        <meta name="twitter:title" content={metadata.openGraph.title} />
        <meta name="twitter:description" content={metadata.openGraph.description} />
        <meta name="twitter:image" content={metadata.openGraph.image} />
        <meta name="twitter:image:alt" content={`SNOWBLOWR - On-Demand Snow Removal in ${city.name}`} />
        <meta name="twitter:site" content="@snowblowr" />
        <meta name="twitter:creator" content="@snowblowr" />
      </Helmet>

      {/* Hero Section - City Specific */}
      <section className="relative bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0f_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0f_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        {/* Gradient Orbs */}
        <motion.div 
          className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-500/30 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative container-wide mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            {/* City Badge */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500/20 backdrop-blur-sm border border-accent-500/30 rounded-full mb-6"
            >
              <MapPin className="w-4 h-4 text-accent-500" />
              <span className="text-sm font-bold text-accent-500 uppercase tracking-wider">
                Now Serving {city.name}
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-[0.95]"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              SNOWBLOWR in<br />
              <span className="gradient-text">{city.name}</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              className="text-xl md:text-2xl text-snow-100 mb-8 max-w-3xl mx-auto leading-relaxed font-semibold"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {city.name}'s #1 On-Demand Snow Removal Service.<br />
              <span className="text-accent-500">Your driveway cleared in 2 hours. No contracts. No BS.</span>
            </motion.p>

            {/* City Stats */}
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <Snowflake className="w-6 h-6 text-accent-500 mx-auto mb-2" />
                <p className="text-2xl font-black">{city.avgSnowfall}cm</p>
                <p className="text-xs text-snow-200 font-semibold">Avg Snowfall</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <Users className="w-6 h-6 text-accent-500 mx-auto mb-2" />
                <p className="text-2xl font-black">{city.uniqueSellPoints.length > 0 ? city.uniqueSellPoints[0].match(/\d+/)?.[0] + '+' : '50+'}</p>
                <p className="text-xs text-snow-200 font-semibold">Local Operators</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <Clock className="w-6 h-6 text-accent-500 mx-auto mb-2" />
                <p className="text-2xl font-black">2hr</p>
                <p className="text-xs text-snow-200 font-semibold">Avg Response</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <Star className="w-6 h-6 text-accent-500 mx-auto mb-2" fill="currentColor" />
                <p className="text-2xl font-black">4.9</p>
                <p className="text-xs text-snow-200 font-semibold">App Rating</p>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Button 
                variant="primary" 
                size="lg" 
                onClick={handleGetApp}
                className="group relative overflow-hidden bg-accent-500 hover:bg-accent-600 shadow-glow-green text-base font-bold px-8 py-6"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Get SNOWBLOWR in {city.name}
                  <motion.span
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    →
                  </motion.span>
                </span>
              </Button>
              <Button 
                variant="secondary" 
                size="lg" 
                onClick={handleQuoteClick}
                className="bg-white text-brand-600 hover:bg-neutral-50 border-3 border-white text-base font-black px-8 py-6"
              >
                See Pricing
              </Button>
            </motion.div>

            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex items-center justify-center gap-4 text-sm text-snow-200"
            >
              <div className="flex items-center gap-1">
                <Check className="w-4 h-4 text-accent-500" strokeWidth={3} />
                <span>No Contracts</span>
              </div>
              <div className="flex items-center gap-1">
                <Check className="w-4 h-4 text-accent-500" strokeWidth={3} />
                <span>Transparent Pricing</span>
              </div>
              <div className="flex items-center gap-1">
                <Check className="w-4 h-4 text-accent-500" strokeWidth={3} />
                <span>Verified Operators</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why SNOWBLOWR in [City] */}
      <section className="py-16 md:py-24 bg-neutral-50">
        <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="brand" className="mb-4">BUILT FOR {city.name.toUpperCase()}</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4">
                Why {city.name} homeowners<br />
                <span className="gradient-text">choose SNOWBLOWR</span>
              </h2>
              <p className="text-lg text-neutral-700 font-medium max-w-2xl mx-auto">
                {city.seasonalNotes}
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {city.uniqueSellPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card variant="solid" className="h-full text-center p-6">
                  <div className="w-12 h-12 rounded-full bg-brand-500 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-6 h-6 text-white" strokeWidth={3} />
                  </div>
                  <p className="font-bold text-neutral-900 leading-relaxed">{point}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Neighborhoods We Serve */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="accent" className="mb-4">COMPREHENSIVE COVERAGE</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4">
                Neighborhoods we serve<br />
                <span className="gradient-text">across {city.name}</span>
              </h2>
              <p className="text-lg text-neutral-700 font-medium max-w-2xl mx-auto">
                From {city.keyNeighborhoods[0]} to {city.keyNeighborhoods[city.keyNeighborhoods.length - 1]}, 
                we've got {city.name} covered.
              </p>
            </motion.div>
          </div>

          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {city.keyNeighborhoods.map((neighborhood, index) => (
                <motion.div
                  key={neighborhood}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="bg-neutral-50 rounded-xl p-4 text-center border-2 border-neutral-200 hover:border-brand-500 hover:shadow-lg transition-all"
                >
                  <MapPin className="w-5 h-5 text-brand-500 mx-auto mb-2" />
                  <p className="font-bold text-neutral-900 text-sm">{neighborhood}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-neutral-600 font-medium mb-4">
                Don't see your neighborhood? We're expanding fast across {city.name}!
              </p>
              <Button
                variant="outline"
                size="lg"
                onClick={handleGetApp}
                className="group"
              >
                Check Availability
                <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works - City Specific */}
      <section className="py-16 md:py-24 bg-neutral-50">
        <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="brand" className="mb-4">SIMPLE PROCESS</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4">
                Snow removal in {city.name}<br />
                <span className="gradient-text">made stupid simple</span>
              </h2>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                step: '1',
                icon: <Phone className="w-8 h-8" />,
                title: 'Download & Book',
                description: `Download SNOWBLOWR, add your ${city.name} address, and book a clear when it snows.`
              },
              {
                step: '2',
                icon: <Zap className="w-8 h-8" />,
                title: 'Get Matched Fast',
                description: `We match you with a verified operator near you in ${city.name} within minutes.`
              },
              {
                step: '3',
                icon: <Check className="w-8 h-8" />,
                title: 'Cleared & Done',
                description: `Your ${city.name} driveway gets cleared, you get photo proof, and payment is automatic.`
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Card variant="solid" className="text-center p-8 h-full">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center mx-auto mb-4 text-white font-black text-2xl">
                    {item.step}
                  </div>
                  <div className="w-12 h-12 rounded-full bg-accent-500/10 flex items-center justify-center mx-auto mb-4 text-accent-500">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-black mb-3">{item.title}</h3>
                  <p className="text-neutral-700 font-medium">{item.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing for City */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="accent" className="mb-4">TRANSPARENT PRICING</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4">
                {city.name} snow removal<br />
                <span className="gradient-text">pricing that makes sense</span>
              </h2>
              <p className="text-lg text-neutral-700 font-medium max-w-2xl mx-auto">
                See your exact price before booking. No surprises, no hidden fees.
              </p>
            </motion.div>
          </div>

          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card variant="solid" className="p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500/10 rounded-full mb-4">
                    <DollarSign className="w-5 h-5 text-accent-500" />
                    <span className="font-black text-accent-600 text-sm uppercase tracking-wider">Average Price</span>
                  </div>
                  <div className="mb-6">
                    <p className="text-sm text-neutral-600 font-bold uppercase tracking-wider mb-2">Starting from</p>
                    <p className="text-6xl font-black gradient-text mb-2">$30</p>
                    <p className="text-neutral-700 font-semibold">for standard driveway in {city.name}</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-accent-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                      <p className="text-sm font-medium text-neutral-700">Price shown before you book</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-accent-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                      <p className="text-sm font-medium text-neutral-700">Factors: driveway size, snow depth, location in {city.name}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-accent-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                      <p className="text-sm font-medium text-neutral-700">No hidden fees or surprise charges</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-accent-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                      <p className="text-sm font-medium text-neutral-700">Add walkways, stairs, or sidewalks for upfront cost</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-brand-500/5 to-accent-500/5 rounded-2xl p-8 border-2 border-brand-500/20">
                  <h3 className="text-xl font-black mb-4 text-center">Get Your {city.name} Price</h3>
                  <div className="space-y-4 mb-6">
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <p className="text-xs font-bold text-neutral-600 uppercase tracking-wider mb-1">Small Driveway</p>
                      <p className="text-2xl font-black text-brand-500">$30-40</p>
                      <p className="text-xs text-neutral-600">1 car, &lt;10cm snow</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <p className="text-xs font-bold text-neutral-600 uppercase tracking-wider mb-1">Medium Driveway</p>
                      <p className="text-2xl font-black text-brand-500">$40-60</p>
                      <p className="text-xs text-neutral-600">2 car, 10-20cm snow</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <p className="text-xs font-bold text-neutral-600 uppercase tracking-wider mb-1">Large Driveway</p>
                      <p className="text-2xl font-black text-brand-500">$60-90</p>
                      <p className="text-xs text-neutral-600">3+ car, 20cm+ snow</p>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleQuoteClick}
                    className="w-full"
                  >
                    Get My Exact Quote
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="py-16 md:py-24 bg-neutral-50">
        <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="brand" className="mb-4">TRUST & SAFETY</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4">
                Every operator in {city.name}<br />
                <span className="gradient-text">is verified & insured</span>
              </h2>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: <Shield className="w-8 h-8" />,
                title: 'Background Checked',
                description: `Every operator in ${city.name} passes identity verification and background screening.`
              },
              {
                icon: <Star className="w-8 h-8" />,
                title: 'Rated & Reviewed',
                description: `See real ratings from ${city.name} homeowners before your clear.`
              },
              {
                icon: <DollarSign className="w-8 h-8" />,
                title: 'Payment Protected',
                description: `Money held until job is done right. Refunds if service doesn't meet standards.`
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Card variant="solid" className="text-center p-8 h-full">
                  <div className="w-16 h-16 rounded-full bg-brand-500 flex items-center justify-center mx-auto mb-4 text-white">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-black mb-3">{item.title}</h3>
                  <p className="text-neutral-700 font-medium">{item.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white">
        <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-6">
              Ready to never shovel<br />
              <span className="gradient-text">your {city.name} driveway again?</span>
            </h2>
            <p className="text-xl text-snow-100 mb-8 font-semibold">
              Join thousands of {city.name} homeowners who've ditched their shovels for good.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                variant="primary" 
                size="lg" 
                onClick={handleGetApp}
                className="group relative overflow-hidden bg-accent-500 hover:bg-accent-600 shadow-glow-green text-base font-bold px-8 py-6"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Download SNOWBLOWR Now
                  <motion.span
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    →
                  </motion.span>
                </span>
              </Button>
            </div>
            <p className="mt-6 text-sm text-snow-200 font-medium">
              Available on iOS and Android • No credit card required to download
            </p>
          </motion.div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-white">
        <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-black mb-6">About Snow Removal in {city.name}, {city.provinceCode}</h2>
            <p className="text-neutral-700 font-medium leading-relaxed mb-6">
              {city.name} experiences an average of {city.avgSnowfall}cm of snowfall annually, making reliable snow removal 
              essential for homeowners across the region. {city.seasonalNotes} That's why SNOWBLOWR has built a network of 
              verified operators specifically for {city.name} residents who need fast, affordable, and transparent snow clearing services.
            </p>
            <p className="text-neutral-700 font-medium leading-relaxed mb-6">
              Traditional snow removal companies in {city.name} often require seasonal contracts, have unpredictable pricing, 
              and offer limited visibility into when service will arrive. SNOWBLOWR changes this by providing on-demand booking, 
              real-time tracking, upfront pricing, and photo verification of completed work—all through an easy-to-use mobile app.
            </p>
            <h3 className="text-2xl font-black mb-4">Why Choose SNOWBLOWR for {city.name} Snow Removal?</h3>
            <ul className="space-y-2 mb-6">
              {city.uniqueSellPoints.map((point, index) => (
                <li key={index} className="text-neutral-700 font-medium flex items-start gap-2">
                  <Check className="w-5 h-5 text-accent-500 flex-shrink-0 mt-1" strokeWidth={3} />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            <p className="text-neutral-700 font-medium leading-relaxed">
              Whether you're in {city.keyNeighborhoods[0]}, {city.keyNeighborhoods[1]}, or anywhere else across {city.name}, 
              SNOWBLOWR connects you with local operators who know the area and are ready to clear your driveway within hours of 
              snowfall. Download the app today and experience the future of snow removal in {city.name}.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

